// Forked from ../../ResultsDropdown/GroupAssets.tsx — keep in sync until phase_0 cleanup.
import React from 'react';
import { useTranslate } from '@bynder/localization';
import { Badge } from '@bynder/design-system';
import type { AssetsSearchResults } from '../../types';
import { getMatchExplanation, onItemClick } from '../../helpers/spotlightAssetActions';
import { useSpotlightCapabilities } from '../../hooks/useSpotlightCapabilities';
import { ListRow } from './ListRow';
import { ImageWithPreviewPopover } from './ImageWithPreviewPopover';
import { getSpotlightResultClassName } from '../../helpers/getSpotlightResultClassName';
import {
	EXPLAINABILITY_BADGE_LABEL_KEYS,
	deriveExplainabilityBadge,
} from '../../HybridSearch/explainabilityBadge';
import { HighlightedText } from '../HighlightedText';
import { useSearch } from '../../SpotlightState';

interface Props {
	assetResults: AssetsSearchResults;
}

// The Assets category's rows only — rendered inside ResultsSection's shared heading/list chrome.
export function GroupAssets({ assetResults }: Props) {
	const { searchTerm, addSearchTermToRecentSearches } = useSearch();
	const { translate } = useTranslate();
	const { canShowRecentSearches } = useSpotlightCapabilities();

	return (
		<>
			{assetResults.items.map((asset) => {
				const extensionList = asset.extension?.join(', ').toUpperCase() || '';
				const matchExplanation = getMatchExplanation(asset, translate);
				const explainabilityBadge = deriveExplainabilityBadge(asset.highlight);
				const badges = [
					explainabilityBadge && (
						<Badge isSecondary isSmall hasBorder key="explainability">
							{translate(EXPLAINABILITY_BADGE_LABEL_KEYS[explainabilityBadge])}
						</Badge>
					),
					extensionList.length > 0 && (
						<Badge isSecondary isSmall key="extension">
							{extensionList}
						</Badge>
					),
				].filter(Boolean);

				return (
					<ListRow
						dataId={asset.id}
						dataSrc={asset.thumbnails?.large}
						afterSelect="close"
						onSelect={() =>
							onItemClick(asset.id, {
								highlight: asset.highlight,
								searchTerm,
								canShowRecentSearches,
								addSearchTermToRecentSearches,
							})
						}
						className={getSpotlightResultClassName('Asset')}
						key={asset.id}
						thumbnail={
							<ImageWithPreviewPopover
								imageSrcMini={asset.thumbnails?.mini || ''}
								imageSrcLarge={asset.thumbnails?.large}
							/>
						}
						title={asset.name}
						subtext={
							matchExplanation.length > 0 && (
								<HighlightedText segments={matchExplanation} />
							)
						}
						rightElements={badges.length > 0 ? badges : undefined}
					/>
				);
			})}
		</>
	);
}
